import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import PropTypes from 'prop-types'

// Components
import { Button } from '@/components/elements/button'
import { Skeleton } from '@/components/elements/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/elements/table'

import { DataTablePagination } from './DataTablePagination'

const DEFAULT_COLUMN_SIZE = 200

/**
 *
 * DataTable
 *
 * - Displays a table of data
 */
const CustomDataTable = ({
  columns,
  data,
  filters = [],
  loading = false,
  pagination = { pageIndex: 0, pageSize: 20 },
  rowCount,
  setPagination = null,
  hidePagination = false,
  sorting = [],
  setSorting = null,
  rowClassName = null,
}) => {
  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      size: DEFAULT_COLUMN_SIZE,
      minSize: 50,
      maxSize: 300,
    },
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    state: {
      columnFilters: filters,
      pagination,
      sorting,
    },
  })

  const renderTableBody = () => {
    if (loading) {
      // Skeleton rows mirroring the real column layout so the table doesn't
      // collapse/jump when data arrives.
      const leafColumns = table.getVisibleLeafColumns()
      return ['sk-1', 'sk-2', 'sk-3', 'sk-4', 'sk-5'].map((rowKey) => (
        <TableRow key={rowKey}>
          {leafColumns.map((column) => {
            const size = column.getSize()
            return (
              <TableCell
                key={column.id}
                style={{
                  minWidth: `${size}px`,
                  ...(size === DEFAULT_COLUMN_SIZE && {
                    width: '100%',
                    flex: 1,
                  }),
                }}
              >
                <Skeleton className="h-4 w-3/4 max-w-40" />
              </TableCell>
            )
          })}
        </TableRow>
      ))
    }

    return table.getRowModel().rows?.length ? (
      table.getRowModel().rows.map((row) => (
        <TableRow
          key={row.id}
          data-state={row.getIsSelected() && 'selected'}
          className={typeof rowClassName === 'function' ? rowClassName({ row }) : rowClassName}
        >
          {row.getVisibleCells().map((cell) => {
            const size = cell.column.getSize()
            return (
              <TableCell
                key={cell.id}
                style={{
                  minWidth: `${size}px`,
                  ...(size === DEFAULT_COLUMN_SIZE && {
                    width: '100%',
                    flex: 1,
                  }),
                }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            )
          })}
        </TableRow>
      ))
    ) : (
      <TableRow>
        <TableCell colSpan={columns.length} className="h-24 text-center">
          No data.
        </TableCell>
      </TableRow>
    )
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="w-full rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const size = header.column.getSize()
                  return (
                    <TableHead
                      key={header.id}
                      className="px-2.5 uppercase has-[>button]:px-0"
                      style={{
                        minWidth: `${size}px`,
                        ...(size === DEFAULT_COLUMN_SIZE && {
                          width: '100%',
                          flex: 1,
                        }),
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>{renderTableBody()}</TableBody>
        </Table>
      </div>
      {!hidePagination && <DataTablePagination table={table} />}
    </div>
  )
}

CustomDataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  filters: PropTypes.array,
  loading: PropTypes.bool,
  pagination: PropTypes.object,
  rowCount: PropTypes.number.isRequired,
  setPagination: PropTypes.func,
  sorting: PropTypes.array,
  setSorting: PropTypes.func,
  hidePagination: PropTypes.bool,
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
}

export default CustomDataTable

export const ColumnHeader = ({ label }) => (
  <Button variant="ghost" size="sm" className="cursor-default hover:bg-transparent">
    <span className="uppercase">{label}</span>
  </Button>
)

ColumnHeader.propTypes = {
  label: PropTypes.string.isRequired,
}

export const SortableColumnHeader = ({ column, label }) => {
  let sortIcon = <ArrowUpDown className="ml-2 h-4 w-4" />

  if (column.getIsSorted() === 'asc') {
    sortIcon = <ArrowUp className="ml-2 h-4 w-4" />
  } else if (column.getIsSorted() === 'desc') {
    sortIcon = <ArrowDown className="ml-2 h-4 w-4" />
  }

  return (
    <Button
      variant="ghost"
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      size="sm"
    >
      <span className="uppercase">{label}</span>
      {sortIcon}
    </Button>
  )
}

SortableColumnHeader.propTypes = {
  column: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
}
